import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from "react";
import { useForm, type Resolver, type SubmitHandler } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { formatReferralCode, registerFormSchema, type RegisterForm } from "@nh/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AuthCard,
  AuthLayout,
  Form,
  FormError,
  Stepper,
  WizardNav,
  useWizard,
} from "@/shared/ui/core";
import { useAuth } from "./auth.context";
import { fieldForErrorCode } from "./auth.errors";
import { AuthSwitchLink } from "./components/AuthSwitchLink";
import { GoogleSection } from "./components/GoogleSection";
import {
  AccessStep,
  ConfirmStep,
  IdentityStep,
} from "./register/RegisterStepBodies";
import {
  EMPTY_REGISTER_FORM,
  fieldsOfStep,
  REGISTER_FIELDS,
  REGISTER_STEPS,
  REGISTER_STEP_LABELS,
  STEP_ACCESS,
  STEP_CONFIRM,
  STEP_IDENTITY,
  type RegisterFormValues,
} from "./register/registerSteps";
import { useAuthSubmit } from "./useAuthSubmit";
import { clearReferral, readReferral } from "@/features/network/referral.storage";

/**
 * El alta, en tres pasos: quién eres · tu acceso · confirmación.
 *
 * ── UN formulario, no tres ──────────────────────────────────────────
 * Hay un solo `useForm` para todo el wizard. Un formulario por paso
 * obligaría a copiar los valores de uno a otro a mano y, el día que
 * alguien retrocediera a corregir una letra, encontraría el paso vacío.
 * Con uno solo, ir y volver no pierde nada porque nunca se desmontó el
 * estado — solo los campos.
 *
 * ── UNA petición, al final ──────────────────────────────────────────
 * Los pasos son interfaz. La cuenta se crea en una sola llamada desde el
 * paso 3: partir el envío dejaría cuentas a medio crear cada vez que
 * alguien abandona en el dos.
 *
 * ── Validar solo lo que ya se vio ───────────────────────────────────
 * "Siguiente" valida ÚNICAMENTE los campos de su paso. Enviar el
 * formulario entero para avanzar sacaría en rojo el correo y la
 * contraseña de alguien que todavía va escribiendo su apellido.
 *
 * ── Y volver al paso donde está el error ────────────────────────────
 * Si el servidor señala un campo del paso 2 estando en el 3, se regresa
 * a ese paso con el mensaje puesto y el foco en el campo. Un error
 * pintado en un paso oculto es un botón de crear cuenta que no hace nada
 * y nadie sabe por qué.
 */
export function RegisterPage() {
  const { register: createAccount } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  /*
   * Mismo trato que el login: quien llegó aquí desde el carrito vuelve
   * al carrito. El carrito en sí no necesita rescate — vive en
   * `localStorage` y el registro no lo toca.
   */
  const from = (location.state as { from?: string } | null)?.from;

  /*
   * La invitación, si se llegó por `/r/CODE` (Anahí, 27-ago). Esa
   * pantalla ya validó forma y referencia; aquí se ENSEÑA quién invita
   * —con la opción de quitarlo— y el código viaja en el alta. El
   * servidor lo vuelve a validar todo: esto es el camino, no la
   * garantía.
   */
  const [referral, setReferral] = useState(() => readReferral());

  const form = useForm<RegisterFormValues, unknown, RegisterForm>({
    /*
     * El `as` documenta una diferencia real, no la tapa.
     *
     * `zodResolver` se tipa como "recibe lo que el esquema ACEPTA", y el
     * esquema acepta `country: "MX" | "US" | "OTHER"`. Pero un formulario a
     * medio llenar tiene `country: ""` —el hueco de "todavía no elegí"— y
     * eso es precisamente lo que hay que poder pasarle para que responda
     * "Elige tu país". En tiempo de ejecución lo maneja perfectamente: un
     * `z.enum` rechaza `""` como rechaza cualquier otra cosa que no esté en
     * la lista. Lo que no encaja es la varianza de los tipos, no el
     * comportamiento.
     */
    resolver: zodResolver(registerFormSchema) as Resolver<
      RegisterFormValues,
      unknown,
      RegisterForm
    >,
    defaultValues: EMPTY_REGISTER_FORM,
    mode: "onSubmit",
    // Tres genéricos a propósito: lo que el formulario TIENE mientras se
    // llena (`acceptedTerms` empieza en false, `country` en "") no es lo
    // que el esquema DEVUELVE al validar. El tercero es la salida ya
    // validada, que es lo que recibe el envío.
  });

  /*
   * El permiso para avanzar: validar los campos de ESTE paso y nada más.
   *
   * `trigger` con una lista de nombres corre el esquema completo pero solo
   * PINTA —y solo reporta— los errores de esos nombres. Es exactamente lo
   * que hace falta: la regla de "las contraseñas coinciden" vive en el
   * esquema entero, así que se comprueba de verdad, pero el correo vacío
   * del paso 2 no aparece en rojo mientras se llena el paso 1.
   */
  const validateStep = useCallback(
    (step: number) => form.trigger([...fieldsOfStep(step)]),
    [form],
  );

  const wizard = useWizard({
    total: REGISTER_STEPS.length,
    canAdvance: validateStep,
  });

  const { submit, formError, dismissFormError } = useAuthSubmit({
    form,
    fields: REGISTER_FIELDS,
    fieldForCode: fieldForErrorCode,
    // El payload se arma campo por campo y NO reenviando `values`:
    // `confirmPassword` es asunto de la interfaz, y mandarlo sería filtrar
    // la contraseña un segundo lugar más en los logs del servidor.
    action: ({ firstName, lastName, country, phone, email, password, acceptedTerms }) =>
      createAccount({
        firstName,
        lastName,
        country,
        phone,
        email,
        password,
        acceptedTerms,
        // El código de la invitación, si la hubo. El servidor lo valida
        // referencial y da el alta en la red como CLIENTE.
        ...(referral ? { referralCode: referral.code } : {}),
      }),
    /*
     * Al LOGIN, no adentro (Anahí, 26-ago): la cuenta quedó creada y
     * ahora se entra con ella. `justRegistered` es lo que le permite al
     * login dar la bienvenida en vez de recibirla en seco, y el `from`
     * sigue viajando para que quien venía del carrito acabe en el
     * carrito después de entrar.
     */
    onSuccess: () => {
      // La invitación ya cumplió: no debe arrastrarse a un segundo
      // registro de la misma pestaña.
      clearReferral();
      navigate("/login", { replace: true, state: { from, justRegistered: true } });
    },
  });

  /**
   * Envía y, si el servidor devolvió el fallo señalando un campo, lleva de
   * vuelta al paso de ese campo.
   *
   * Se lee `getFieldState` y no `formState.errors` a propósito:
   * `formState` es una instantánea de este render, y estos errores los
   * acaba de poner `useAuthSubmit` un instante antes: la instantánea
   * todavía no los tiene.
   */
  const handleSubmit: SubmitHandler<RegisterForm> = async (values) => {
    await submit(values);

    const stepWithError = REGISTER_STEPS.findIndex((step) =>
      step.fields.some((field) => form.getFieldState(field).error),
    );
    if (stepWithError >= 0) wizard.goTo(stepWithError);
  };

  /**
   * Enter avanza de paso; no envía a medias.
   *
   * En los pasos intermedios no hay botón de envío, así que la mayoría de
   * los navegadores ya no enviarían. Pero "la mayoría" no es una garantía
   * —el envío implícito depende de cuántos campos haya— y el día que un
   * paso se quede con uno solo, Enter mandaría el formulario entero y
   * sacaría en rojo campos que nadie ha visto. Aquí Enter significa
   * siempre lo mismo: seguir.
   *
   * Los botones y los enlaces quedan fuera: en ellos Enter ya significa
   * "actívame", y robárselo rompería "Atrás" y los enlaces de los
   * términos para quien navega con teclado.
   */
  const handleKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== "Enter" || wizard.isLast) return;

    const target = event.target as HTMLElement;
    if (
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement
    ) {
      return;
    }

    event.preventDefault();
    void wizard.next();
  };

  const stepRegionRef = useRef<HTMLDivElement>(null);
  useStepFocus(wizard.step, form, stepRegionRef);

  return (
    // `md`, el mismo ancho que el login. Con los campos en una sola
    // columna, `lg` daba casillas de casi 600 px para escribir un nombre y
    // una card que tapaba el centro de la fotografía del fondo.
    <AuthLayout width="md">
      <AuthCard
        compact
        title="Crea tu cuenta"
        footer={
          <AuthSwitchLink
            prompt="¿Ya tienes una cuenta?"
            linkLabel="Inicio de sesión"
            to="/login"
            state={from ? { from } : undefined}
          />
        }
      >
        {/*
          "Te invita X": la señal de que el enlace funcionó, con la
          opción de registrarse SIN patrocinador — traer código dice de
          quién cuelgas, nunca es una obligación.
        */}
        {referral && (
          <div className="mb-1 flex items-center justify-between gap-3 rounded-2xl border border-ds-violet-pop/40 bg-ds-mist/50 px-4 py-2.5">
            <p className="text-[13px] text-ds-ink">
              Te invita <span className="font-semibold">{referral.displayName}</span>{" "}
              <span className="font-mono text-[12px] text-ds-ink-3">
                · {formatReferralCode(referral.code)}
              </span>
            </p>
            <button
              type="button"
              aria-label="Registrarme sin invitación"
              onClick={() => {
                clearReferral();
                setReferral(null);
              }}
              className="rounded-full p-1 text-ds-ink-3 transition hover:bg-ds-mist hover:text-ds-ink"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        <FormError error={formError} onDismiss={dismissFormError} />

        <Form
          form={form}
          onSubmit={handleSubmit}
          onKeyDown={handleKeyDown}
          // `gap-4` y no `gap-5`: con los campos en una sola columna, la
          // card es alta y cada separador de más empuja el botón hacia
          // fuera de la pantalla. Es el mismo ritmo que hay entre campos.
          className="grid gap-4"
        >
          <Stepper
            steps={REGISTER_STEP_LABELS}
            current={wizard.step}
            label="Progreso del registro"
          />

          {/*
            La columna del paso. UNA sola, en cualquier tamaño de pantalla:
            el alta se partió en pasos precisamente para no tener que
            repartir los campos a lo ancho, así que dos columnas aquí
            anularían la razón de ser del wizard.

            El `min-h` es la reserva que evita el salto de alto. Los tres
            pasos no miden lo mismo —cuatro campos, tres campos, un resumen
            de cuatro renglones más los términos— y sin ella la card se
            encogía a cada "Siguiente", moviendo el botón bajo el dedo
            justo cuando alguien va a pulsarlo.

            Está calibrada al paso 2, no al 1. Igualar los tres al primero
            costaría ~130 px de hueco vacío en el resumen, y el paso 1 va a
            ser más alto de todas formas: es el único que lleva el bloque
            de Google. Así los pasos 2 y 3 miden EXACTAMENTE lo mismo (624
            px de card) y sus botones no se mueven ni un píxel; el paso 1
            queda más alto por una razón que se ve en pantalla — hay una
            opción más ahí.

            El medidor de contraseña cabe dentro de la reserva: el paso 2
            no crece al desplegarlo.

            `outline-none` porque este contenedor recibe el foco al cambiar
            de paso (ver `useStepFocus`) y un contorno alrededor de medio
            formulario no dice nada; lo que informa es que el lector de
            pantalla anuncie el grupo.
          */}
          <div
            ref={stepRegionRef}
            tabIndex={-1}
            role="group"
            aria-label={REGISTER_STEP_LABELS[wizard.step]}
            className="grid min-h-[18rem] items-start gap-3 outline-none"
          >
            {wizard.step === STEP_IDENTITY && <IdentityStep />}
            {wizard.step === STEP_ACCESS && <AccessStep />}
            {wizard.step === STEP_CONFIRM && <ConfirmStep onEdit={wizard.goTo} />}
          </div>

          <WizardNav
            onBack={wizard.isFirst ? undefined : wizard.back}
            onNext={() => void wizard.next()}
            isLast={wizard.isLast}
            submitLabel="Crear cuenta"
          />
        </Form>

        {/*
          Google SOLO en el primer paso. Entrar con Google salta el
          formulario entero; ofrecerlo en el paso 3, cuando ya se llenó
          todo, es enseñar un atajo cuando ya no hay nada que atajar.
        */}
        {wizard.isFirst && (
          <GoogleSection
            label="Regístrate con Google"
            intent="signup"
            disabled={form.formState.isSubmitting}
            // Sin divisor: el botón viene justo detrás de "Siguiente", así
            // que ya se lee como la otra vía. En el login sí lo lleva,
            // porque allí separa dos caminos equivalentes.
            withDivider={false}
          />
        )}
      </AuthCard>
    </AuthLayout>
  );
}

/**
 * A dónde va el foco al cambiar de paso.
 *
 * Sin esto, el foco se queda en el botón "Siguiente" —que acaba de
 * cambiar de significado bajo el cursor— y quien usa lector de pantalla no
 * se entera de que la mitad de la card se sustituyó.
 *
 * Si el paso al que se llega trae un error (el `EMAIL_TAKEN` que devolvió
 * desde el paso 3, por ejemplo), el foco va A ESE CAMPO: es lo que hay que
 * corregir y es donde está el mensaje. Si no, al grupo del paso, que se
 * anuncia con su nombre.
 *
 * En el primer render no se toca nada: ahí manda el `autoFocus` del primer
 * campo, y robárselo dejaría el foco en un contenedor en vez de en la
 * casilla donde se empieza a escribir.
 */
function useStepFocus(
  step: number,
  form: ReturnType<typeof useForm<RegisterFormValues, unknown, RegisterForm>>,
  region: RefObject<HTMLDivElement | null>,
) {
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const errored = fieldsOfStep(step).find(
      (field) => form.getFieldState(field).error,
    );

    if (errored) {
      form.setFocus(errored);
      return;
    }

    region.current?.focus();
  }, [form, region, step]);
}
